/**
 * Authentication Store
 *
 * Follows SOLID Principles:
 * - Single Responsibility: Manages authentication state only
 * - Open/Closed: Extensible through store actions, closed for modification
 * - Dependency Inversion: Depends on secure storage abstraction
 *
 * Uses Zustand for state management with secure token storage.
 */

import { create } from 'zustand';
import { persist, PersistStorage } from 'zustand/middleware';
import {
  setSecureToken,
  getSecureToken,
  removeSecureToken,
  TOKEN_KEYS,
  migrateLegacyTokens,
} from '@/utils/secureTokenStorage';

// ============================================================================
// Interfaces (Interface Segregation Principle)
// ============================================================================

/**
 * Authentication state interface
 */
interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  loginINput: string | null;
  isInitialized: boolean;

  // Actions
  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  setLoginInput: (input: string) => void;
  clearTokens: () => void;
  clearLoginINput: () => void;
  initializeTokens: () => void;
  /** Sync in-memory tokens from secure storage (e.g. after refresh before rehydration) */
  syncTokensFromStorage: () => void;
}

/**
 * Storage value type for Zustand persist
 */
type StorageValue<T> = {
  state: T;
  version: number;
};

// ============================================================================
// Secure Storage Implementation (Dependency Inversion)
// ============================================================================

/**
 * Creates a secure storage adapter for Zustand persist
 * Single Responsibility: Handles Zustand persistence with secure token storage
 * Dependency Inversion: Implements Zustand's storage interface
 */
const createSecureStorage = (): PersistStorage<AuthState> => {
  return {
    getItem: (name: string): StorageValue<AuthState> | null => {
      if (typeof window === 'undefined') return null;

      try {
        // For auth-storage, combine secure tokens with other state
        if (name === 'auth-storage') {
          // Get tokens from secure storage
          const accessToken = getSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
          const refreshToken = getSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
          if (typeof window !== 'undefined') {
            console.log('[AUTH_DEBUG] getItem(auth-storage)', {
              hasAccessToken: !!accessToken,
              hasRefreshToken: !!refreshToken,
              accessTokenLength: accessToken?.length ?? 0,
              refreshTokenLength: refreshToken?.length ?? 0,
            });
          }

          // Get other state from localStorage
          const stored = localStorage.getItem(name);
          const parsed: StorageValue<Partial<AuthState>> = stored
            ? JSON.parse(stored)
            : { state: {}, version: 0 };

          // Merge secure tokens with other state
          return {
            ...parsed,
            state: {
              ...parsed.state,
              accessToken,
              refreshToken,
              isInitialized: parsed.state.isInitialized ?? false,
            },
          } as StorageValue<AuthState>;
        }

        // For other storage, use default localStorage
        const stored = localStorage.getItem(name);
        return stored ? (JSON.parse(stored) as StorageValue<AuthState>) : null;
      } catch (error) {
        console.error(`[AuthStore] Failed to get item ${name}:`, error);
        return null;
      }
    },

    setItem: (name: string, value: StorageValue<AuthState>): void => {
      if (typeof window === 'undefined') return;

      try {
        // For auth-storage, extract tokens and store securely
        if (name === 'auth-storage') {
          const state = value.state;

          if (typeof window !== 'undefined') {
            console.log('[AUTH_DEBUG] setItem(auth-storage)', {
              hasAccessToken: !!state.accessToken,
              hasRefreshToken: !!state.refreshToken,
              accessTokenLength: state.accessToken?.length ?? 0,
              refreshTokenLength: state.refreshToken?.length ?? 0,
            });
          }

          // Store tokens securely (separate from Zustand state)
          if (state.accessToken) {
            setSecureToken(TOKEN_KEYS.ACCESS_TOKEN, state.accessToken);
          } else {
            removeSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
          }

          if (state.refreshToken) {
            setSecureToken(TOKEN_KEYS.REFRESH_TOKEN, state.refreshToken);
          } else {
            removeSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
          }

          // Store other state normally (without tokens)
          // Extract tokens to exclude them from otherState
          const { accessToken, refreshToken, ...otherState } = state;
          // Tokens are intentionally unused here - they're stored separately in secure storage
          void accessToken;
          void refreshToken;
          localStorage.setItem(
            name,
            JSON.stringify({
              ...value,
              state: {
                ...otherState,
                // Don't store tokens in Zustand state - they're in secure storage
                accessToken: null,
                refreshToken: null,
              },
            })
          );
        } else {
          localStorage.setItem(name, JSON.stringify(value));
        }
      } catch (error) {
        console.error(`[AuthStore] Failed to set item ${name}:`, error);
      }
    },

    removeItem: (name: string): void => {
      if (typeof window === 'undefined') return;

      try {
        if (name === 'auth-storage') {
          // Remove secure tokens
          removeSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
          removeSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
        }
        localStorage.removeItem(name);
      } catch (error) {
        console.error(`[AuthStore] Failed to remove item ${name}:`, error);
      }
    },
  };
};

// ============================================================================
// Store Implementation
// ============================================================================

/**
 * Authentication store
 * Single Responsibility: Manages authentication state
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      loginINput: null,
      isInitialized: false,

      /**
       * Sets access token in both store and secure storage
       */
      setAccessToken: (token: string) => {
        setSecureToken(TOKEN_KEYS.ACCESS_TOKEN, token);
        set({ accessToken: token });
      },

      /**
       * Sets refresh token in both store and secure storage
       */
      setRefreshToken: (token: string) => {
        setSecureToken(TOKEN_KEYS.REFRESH_TOKEN, token);
        set({ refreshToken: token });
      },

      /**
       * Sets login input
       */
      setLoginInput: (input: string) => {
        set({ loginINput: input });
      },

      /**
       * Clears all tokens from store and secure storage
       */
      clearTokens: () => {
        removeSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
        removeSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
        set({ accessToken: null, refreshToken: null });
      },

      /**
       * Clears login input
       */
      clearLoginINput: () => {
        set({ loginINput: null });
      },

      /**
       * Initializes tokens by migrating legacy tokens
       * Open/Closed: Can be extended without modifying store logic
       */
      initializeTokens: () => {
        if (get().isInitialized) {
          return; // Already initialized
        }

        // Migrate legacy tokens on initialization
        const migrated = migrateLegacyTokens();
        set({
          accessToken: migrated.accessToken,
          refreshToken: migrated.refreshToken,
          isInitialized: true,
        });
      },

      /**
       * Sync tokens from secure storage into the store.
       * Use when opening the profile menu so menu shows correct state after refresh.
       */
      syncTokensFromStorage: () => {
        if (typeof window === 'undefined') return;
        const accessToken = getSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
        const refreshToken = getSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
        set({ accessToken, refreshToken });
      },
    }),
    {
      name: 'auth-storage',
      storage: createSecureStorage(),
      // Only persist non-sensitive data in Zustand state
      // Tokens are stored separately via secure storage
      partialize: state => ({
        loginINput: state.loginINput,
        isInitialized: state.isInitialized,
        // Tokens are stored separately via secure storage adapter
      }),
    }
  )
);

// ============================================================================
// Initialization (Runs once on module load)
// ============================================================================

/**
 * Initialize tokens on store creation (client-side only)
 * Single Responsibility: Handles initialization only
 */
if (typeof window !== 'undefined') {
  // Run migration on module load
  const store = useAuthStore.getState();
  if (!store.isInitialized) {
    store.initializeTokens();
  }
}
